import { SourceRecord } from "services/types/source-record.type";

/**
 * A view the source viewer panel can show. Every kind maps to one backend endpoint; the panel
 * keeps a stack of these so a related record opens in place with a breadcrumb instead of a
 * new tab.
 */
export type SourceViewerKind =
  | "patient"
  | "physician"
  | "referral_source"
  | "insurer"
  | "chart_note"
  | "appointment"
  | "appointment_type";

export type SourceViewerView = {
  kind: SourceViewerKind;
  id: number;
  label?: string;
};

export const SOURCE_VIEWER_LABELS: Record<SourceViewerKind, string> = {
  patient: "patient",
  physician: "physician",
  referral_source: "referral source",
  insurer: "insurer",
  chart_note: "chart note",
  appointment: "appointment",
  appointment_type: "appointment type"
};

/**
 * Which import targets the panel can open in place. Keyed by the backend's
 * `target_model_name`; anything else (SaleItem, Payment, InsurancePolicy without its patient…)
 * only gets the plain "#id" text.
 */
const TARGET_VIEW_KINDS: Record<string, SourceViewerKind> = {
  Patient: "patient",
  ReferringPhysician: "physician",
  ReferralSource: "referral_source",
  Insurer: "insurer",
  ChartNote: "chart_note",
  Appointment: "appointment",
  AppointmentType: "appointment_type"
};

/** Where the target lives in the app, for the "open in new tab" affordance. */
const TARGET_ROUTES: Record<string, (id: string) => string> = {
  Patient: (id) => `/patients/${id}`,
  ReferringPhysician: (id) => `/settings/physicians/${id}/edit`,
  ReferralSource: (id) => `/settings/referral-sources/${id}/edit`,
  Insurer: () => "/settings/insurers",
  AppointmentType: (id) => `/settings/appointment-types/${id}/edit`
};

export const targetView = (record: SourceRecord): SourceViewerView | null => {
  const kind = TARGET_VIEW_KINDS[record.target_model_name];
  const id = Number(record.target_model_id);
  if (!kind || !record.target_model_id || Number.isNaN(id)) {
    return null;
  }
  return { kind, id };
};

export const targetRoute = (record: SourceRecord): string | null => {
  const route = TARGET_ROUTES[record.target_model_name];
  return route && record.target_model_id ? route(record.target_model_id) : null;
};

/**
 * The one-line reference the migration QA report format asks for: source file, the stable
 * source row id, the MigrationRecord id, and the target model + id.
 */
export const recordReference = (record: SourceRecord): string =>
  [
    record.source_file_name || "(unknown file)",
    record.source_id ? `source_id=${record.source_id}` : null,
    `MigrationRecord #${record.id}`,
    record.target_model_id
      ? `${record.target_model_name} #${record.target_model_id}`
      : `${record.target_model_name} (not imported)`
  ]
    .filter(Boolean)
    .join(" | ");

export const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/** Fixed dialog width: wide enough for a six-column raw table plus its status cell. */
export const SOURCE_VIEWER_WIDTH = 1100;

// ── deep link: ?source_view=<kind>:<id> ───────────────────────────────────────

export const SOURCE_VIEW_PARAM = "source_view";

export const encodeView = (view: SourceViewerView): string =>
  `${view.kind}:${view.id}`;

export const decodeView = (value: string | null): SourceViewerView | null => {
  if (!value) {
    return null;
  }
  const [kind, rawId, extra] = value.split(":");
  const id = Number(rawId);
  if (
    !Object.prototype.hasOwnProperty.call(SOURCE_VIEWER_LABELS, kind) ||
    !Number.isSafeInteger(id) ||
    id <= 0 ||
    extra !== undefined
  ) {
    return null;
  }
  return { kind: kind as SourceViewerKind, id };
};

/** Write or clear the deep-link param without a navigation (history.replaceState). */
export const syncViewToLocation = (view: SourceViewerView | null) => {
  try {
    const url = new URL(window.location.href);
    if (view) {
      url.searchParams.set(SOURCE_VIEW_PARAM, encodeView(view));
    } else {
      url.searchParams.delete(SOURCE_VIEW_PARAM);
    }
    window.history.replaceState(window.history.state, "", url.toString());
  } catch {
    // A sandboxed or test environment without history support — the panel still works.
  }
};

/** Administration › Onboarding tab that lists a target model's migration records. */
export const migrationRecordsPath = (
  targetModelName: string,
  search?: string | null
): string => {
  const slug = targetModelName
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .toLowerCase();
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  return `/administration/onboarding/${slug}${query}`;
};
